import React, { useMemo, useState } from 'react';
import { SearchIcon, ClockIcon, AlertTriangleIcon } from 'lucide-react';
import { User, LicenseData, MichiganLicenseData, US_STATES } from './types';
interface LicenseMatrixProps {
  users: User[];
  licenses: Record<string, Record<string, LicenseData>>;
  michiganLicenses: Record<string, MichiganLicenseData>;
  onCellClick: (userId: string, stateName: string) => void;
  onUserClick: (userId: string) => void;
}
export function LicenseMatrix({
  users,
  licenses,
  michiganLicenses,
  onCellClick,
  onUserClick
}: LicenseMatrixProps) {
  const [search, setSearch] = useState('');
  type StatusFilter = 'all' | 'complete' | 'in_progress' | 'expired';
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const isExpired = (expirationDate: string | null, renewals: {
    year: number;
    fee: number;
    date: string;
  }[]) => {
    if (!expirationDate) return false;
    const exp = new Date(expirationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return exp < today && renewals.length === 0;
  };
  const getStatus = (userId: string, stateName: string): 'not_started' | 'in_progress' | 'complete' | 'expired' => {
    if (stateName === 'Michigan') {
      const mi = michiganLicenses[userId];
      if (!mi) return 'not_started';
      const allComplete = mi.steps.every(Boolean);
      if (allComplete && isExpired(mi.expirationDate, mi.renewals)) return 'expired';
      if (allComplete) return 'complete';
      if (mi.steps.some(Boolean)) return 'in_progress';
      return 'not_started';
    }
    const lic = licenses[userId]?.[stateName];
    if (!lic) return 'not_started';
    if (lic.status === 'complete' && isExpired(lic.expirationDate, lic.renewals)) return 'expired';
    return lic.status;
  };
  const filteredUsers = useMemo(() => {
    let result = users;
    const q = search.toLowerCase().trim();
    if (q) {
      result = result.filter(u => u.name.toLowerCase().includes(q) || u.nmlsId.toLowerCase().includes(q));
    }
    if (statusFilter !== 'all') {
      result = result.filter(u => US_STATES.some(s => getStatus(u.id, s.name) === statusFilter));
    }
    return result;
  }, [users, search, statusFilter, licenses, michiganLicenses]);
  const filterOptions: {
    key: StatusFilter;
    label: string;
    icon: string;
  }[] = [{
    key: 'all',
    label: 'All',
    icon: ''
  }, {
    key: 'complete',
    label: 'Complete',
    icon: '✅'
  }, {
    key: 'in_progress',
    label: 'In Progress',
    icon: '🕐'
  }, {
    key: 'expired',
    label: 'Expired',
    icon: '⚠️'
  }];
  return <div className="flex-1 min-h-0 flex flex-col bg-slate-900/30 border border-slate-800/80 rounded-xl shadow-2xl overflow-hidden" data-id="element-24">
      {/* Header Actions */}
      <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-700/50" data-id="element-25">
        <h2 className="text-sm font-bold text-white uppercase tracking-wider flex-shrink-0" data-id="element-26">
          Loan Officer State Grid
        </h2>

        <div className="flex items-center gap-1 bg-slate-800/40 border border-slate-700/40 rounded-lg p-0.5" data-id="element-27">
          {filterOptions.map(opt => <button key={opt.key} onClick={() => setStatusFilter(opt.key)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${statusFilter === opt.key ? 'bg-slate-600/80 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-28">
              {opt.icon && <span className="mr-1" data-id="element-29">{opt.icon}</span>}
              {opt.label}
            </button>)}
        </div>

        <div className="flex items-center bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-1.5 focus-within:border-emerald-500/50 focus-within:ring-1 focus-within:ring-emerald-500/30 transition-all w-56 flex-shrink-0" data-id="element-30">
          <SearchIcon className="w-3.5 h-3.5 text-slate-500 mr-2 flex-shrink-0" data-id="element-31" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search Officers..." className="w-full bg-transparent text-xs text-slate-200 focus:outline-none placeholder:text-slate-500" data-id="element-32" />
        </div>
      </div>

      {/* Matrix Grid */}
      <div className="flex-1 min-h-0 overflow-auto custom-scrollbar bg-slate-900/50 relative" data-id="element-33">
        <table className="w-full border-collapse text-sm text-left" data-id="element-34">
          <thead className="sticky top-0 z-30 bg-slate-800 text-slate-400 text-xs uppercase tracking-wider" data-id="element-35">
            <tr data-id="element-36">
              <th className="sticky left-0 z-40 bg-slate-800 border-b border-r border-slate-700/50 px-4 py-3 min-w-[180px] max-w-[180px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]" data-id="element-37">
                Officer
              </th>
              <th className="sticky left-[180px] z-40 bg-slate-800 border-b border-r border-slate-700/50 px-4 py-3 min-w-[100px] max-w-[100px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)]" data-id="element-38">
                NMLS ID
              </th>
              {US_STATES.map(state => <th key={state.abbr} className="border-b border-r border-slate-700/50 px-2 py-3 text-center min-w-[44px] max-w-[44px] font-semibold" title={state.name} data-id="element-39">
                  {state.abbr}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50" data-id="element-40">
            {filteredUsers.length === 0 ? <tr data-id="element-41">
                <td colSpan={US_STATES.length + 2} className="px-4 py-8 text-center text-slate-500" data-id="element-42">
                  No users found.
                </td>
              </tr> : filteredUsers.map(user => <tr key={user.id} className="group hover:bg-slate-800/40 transition-colors" data-id="element-43">
                  <td className="sticky left-0 z-20 bg-slate-900 group-hover:bg-slate-800 border-r border-slate-700/50 px-4 py-2.5 font-medium text-slate-200 truncate shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)] transition-colors" data-id="element-44">
                    <button onClick={() => onUserClick(user.id)} className="hover:text-emerald-400 transition-colors text-left w-full truncate" data-id="element-45">
                      {user.name}
                    </button>
                  </td>
                  <td className="sticky left-[180px] z-20 bg-slate-900 group-hover:bg-slate-800 border-r border-slate-700/50 px-4 py-2.5 text-slate-400 tabular-nums shadow-[2px_0_5px_-2px_rgba(0,0,0,0.3)] transition-colors" data-id="element-46">
                    {user.nmlsId}
                  </td>
                  {US_STATES.map(state => {
              const status = getStatus(user.id, state.name);
              return <td key={state.abbr} onClick={() => onCellClick(user.id, state.name)} className="border-r border-slate-700/50 p-0 cursor-pointer hover:bg-slate-700/50 transition-colors text-center align-middle" data-id="element-47">
                        <div className="flex items-center justify-center h-full w-full min-h-[40px]" data-id="element-48">
                          {status === 'complete' && <span className="text-base leading-none" role="img" aria-label="Complete" data-id="element-49">
                              ✅
                            </span>}
                          {status === 'in_progress' && <ClockIcon className="w-4 h-4 text-amber-400" strokeWidth={2.5} data-id="element-50" />}
                          {status === 'expired' && <AlertTriangleIcon className="w-4 h-4 text-red-400" strokeWidth={2.5} data-id="element-51" />}
                        </div>
                      </td>;
            })}
                </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}